/* ============================================================
   Quill&Steel — Rune field
   ------------------------------------------------------------
   A drifting field of rune glyphs that can be attached to any element, so the
   app's aura carries onto the marketing page. Ported from `MagicAura.tsx`:
   same glyph set, same random ranges, same animation.

   Unlike the hero's sparkle field this needs no render loop — the glyphs are
   spans and the browser animates them on the compositor. All the JS does is
   seed the positions once and pause the field while it is off screen.

   Usage:
     - markup: <div data-rune-field data-count="22"> ... </div>
       (auto-attaches on DOM ready and on shopify:section:load)
     - script: QSRuneField.attach(el, { count: 22 })

   The `||` below is load-bearing. Every section that uses a field asks for
   this script, and a section emits its tag once per render — seven times on
   the homepage. The browser fetches once but executes every tag, so without
   the short-circuit the engine would be rebuilt seven times and would add
   seven sets of section listeners. Guarding here rather than hoisting the tag
   into the layout keeps the script off the pages that never use it.
   ============================================================ */

window.QSRuneField = window.QSRuneField || (function () {
	"use strict";

	var RUNES = ["ᚱ", "ᚢ", "ᚾ", "ᛊ", "ᚺ", "ᚦ", "✦", "⟡", "◈", "✧", "⋆", "᛫"];

	var DEFAULT_COUNT = 22;

	var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

	function seed(layer, count) {
		var frag = document.createDocumentFragment();

		for (var i = 0; i < count; i++) {
			var span = document.createElement("span");
			span.className = "qs-rune";
			span.textContent = RUNES[i % RUNES.length];

			var duration = (7 + Math.random() * 8).toFixed(2);
			var delay = (Math.random() * 13).toFixed(2);

			span.style.left = Math.random() * 100 + "%";
			span.style.top = 10 + Math.random() * 88 + "%";
			// Every third glyph runs a little larger, for depth.
			span.style.fontSize = (i % 3 === 0 ? 13 : 10) + "px";
			span.style.animation = "qs-rune-drift " + duration + "s " + delay + "s ease-in-out infinite both";

			frag.appendChild(span);
		}

		layer.appendChild(frag);
	}

	function attach(el, opts) {
		// Purely decorative drifting motion, so reduced-motion users get nothing
		// rather than something slower.
		if (reduceMotion || !el) return null;
		if (el.qsRuneField) return el.qsRuneField;

		opts = opts || {};

		var count = opts.count;
		if (count === undefined) count = parseInt(el.getAttribute("data-count"), 10);
		if (isNaN(count)) count = DEFAULT_COUNT;

		// The same density that reads as a scattering on a desktop panel reads as
		// a swarm on a phone, where the text side is a fraction of the width.
		var w = el.getBoundingClientRect().width;
		if (w && w < 750) count = Math.round(count * 0.45);
		else if (w && w < 1100) count = Math.round(count * 0.7);
		if (count < 1) return null;

		if (getComputedStyle(el).position === "static") el.style.position = "relative";

		var layer = document.createElement("div");
		layer.className = "qs-rune-field";
		layer.setAttribute("aria-hidden", "true");
		seed(layer, count);
		el.insertBefore(layer, el.firstChild);

		var field = { el: el, layer: layer };

		if ("IntersectionObserver" in window) {
			layer.setAttribute("data-paused", "");
			field.io = new IntersectionObserver(
				function (entries) {
					if (entries[0].isIntersecting) layer.removeAttribute("data-paused");
					else layer.setAttribute("data-paused", "");
				},
				{ threshold: 0 }
			);
			field.io.observe(el);
		}

		el.qsRuneField = field;
		return field;
	}

	function attachAll(scope) {
		var nodes = (scope || document).querySelectorAll("[data-rune-field]");
		for (var i = 0; i < nodes.length; i++) attach(nodes[i]);
	}

	function detach(el) {
		var field = el && el.qsRuneField;
		if (!field) return;
		if (field.io) field.io.disconnect();
		if (field.layer.parentNode) field.layer.parentNode.removeChild(field.layer);
		delete el.qsRuneField;
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", function () {
			attachAll();
		});
	} else {
		attachAll();
	}

	// Theme editor tears sections down and rebuilds them.
	document.addEventListener("shopify:section:load", function (e) {
		attachAll(e.target);
	});
	document.addEventListener("shopify:section:unload", function (e) {
		var nodes = e.target.querySelectorAll("[data-rune-field]");
		for (var i = 0; i < nodes.length; i++) detach(nodes[i]);
	});

	return { attach: attach, attachAll: attachAll, detach: detach };
})();
